import React, { useState } from 'react';
import './Contact.css';
import { useLanguage } from '../context/LanguageContext';

// Basit ve güvenli XOR + Base64 simetrik şifreleme anahtarı
const STORAGE_CIPHER_KEY = 'Akinevo_Secret_GeoMap_2026';
const MAP_STORAGE_KEY = 'akinevo_map_view_enc';

const encryptValue = (text) => {
  try {
    const chars = text.split('').map((char, i) =>
      String.fromCharCode(char.charCodeAt(0) ^ STORAGE_CIPHER_KEY.charCodeAt(i % STORAGE_CIPHER_KEY.length))
    ).join('');
    return btoa(chars);
  } catch {
    return btoa(text);
  }
};

const decryptValue = (cipher) => {
  try {
    const decoded = atob(cipher);
    return decoded.split('').map((char, i) =>
      String.fromCharCode(char.charCodeAt(0) ^ STORAGE_CIPHER_KEY.charCodeAt(i % STORAGE_CIPHER_KEY.length))
    ).join('');
  } catch {
    return null;
  }
};

const Contact = () => {
  const { t } = useLanguage();

  // Varsayılan uydu görünümü ('satellite'), kullanıcı değiştirirse şifreli localStorage'dan oku
  const [mapMode, setMapMode] = useState(() => {
    try {
      const savedEnc = localStorage.getItem(MAP_STORAGE_KEY);
      if (savedEnc) {
        const decrypted = decryptValue(savedEnc);
        if (decrypted === 'satellite' || decrypted === 'roadmap') {
          return decrypted;
        }
      }
    } catch (e) {
      // LocalStorage hatasında varsayılan devam et
    }
    return 'satellite'; // Varsayılan uydu görünümü
  });

  const handleMapModeChange = (newMode) => {
    setMapMode(newMode);
    try {
      const encrypted = encryptValue(newMode);
      localStorage.setItem(MAP_STORAGE_KEY, encrypted);
    } catch (e) {
      // Hata yok sayılır
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: 'Web Tasarım & Geliştirme',
    message: '',
    website_hp: '' // Honeypot bot tuzağı
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [validationError, setValidationError] = useState('');

  const subjectOptions = [
    t('contact.subWeb'),
    t('contact.subAndroid'),
    t('contact.subCustom'),
    t('contact.subAdmin'),
    t('contact.subAutomation'),
    t('contact.subMaintenance'),
    t('contact.subPerformance'),
    t('contact.subConsulting'),
    t('contact.subOther')
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (validationError) setValidationError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    // 1. Bot Koruması (Honeypot): Botlar gizli alanı doldurduğunda sahte başarı gösterip isteği iptal et
    if (formData.website_hp) {
      setSubmitted(true);
      return;
    }

    // 2. Spam / Flood Koruması (Rate Limiting): Son gönderimden bu yana 60 saniye geçmemişse engelle
    const lastSubmitTime = localStorage.getItem('akinevo_last_form_submit');
    const now = Date.now();
    if (lastSubmitTime && now - parseInt(lastSubmitTime, 10) < 60000) {
      const remainingSecs = Math.ceil((60000 - (now - parseInt(lastSubmitTime, 10))) / 1000);
      setValidationError(`Lütfen yeni bir mesaj göndermeden önce ${remainingSecs} saniye bekleyin.`);
      return;
    }

    // 3. Katı E-Posta Format Doğrulaması (RFC 5322 uyumlu basit regex)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email.trim())) {
      setValidationError('Lütfen geçerli ve çalışan bir e-posta adresi girin (örn: ornek@alanadi.com).');
      return;
    }

    // 4. Telefon Numarası Format Kontrolü (en az 10 rakam)
    const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
    if (cleanPhone.length < 10) {
      setValidationError('Lütfen geçerli bir telefon numarası girin (en az 10 haneli).');
      return;
    }

    if (formData.name && formData.phone && formData.email && formData.message) {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch('https://formsubmit.co/ajax/akinevo0@gmail.com', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name.trim().slice(0, 70),
            phone: formData.phone.trim().slice(0, 25),
            email: formData.email.trim().slice(0, 100),
            subject: formData.subject,
            message: formData.message.trim().slice(0, 3000),
            _subject: `Yeni İletişim Formu: ${formData.subject} - ${formData.name}`,
            _template: 'table',
            _honey: '', // FormSubmit yerleşik bot tuzağı
            _url: 'https://akinevo.com',
            _blacklist: 'viagra, casino, crypto investment, fast money, loan, porn, sex, adult, pills, lottery'
          })
        });

        if (response.ok) {
          // Gönderim zamanını kaydet (Rate limiting)
          localStorage.setItem('akinevo_last_form_submit', Date.now().toString());
          setSubmitted(true);
          setFormData({
            name: '',
            phone: '',
            email: '',
            subject: subjectOptions[0],
            message: '',
            website_hp: ''
          });
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <section id="iletisim" className="contact-section">
      <div className="section-header">
        <div className="section-badge">
          <i className="fa-solid fa-paper-plane"></i>
          <span>{t('contact.badge')}</span>
        </div>
        <h2 className="section-title">{t('contact.title')}</h2>
        <p className="section-subtitle">{t('contact.subtitle')}</p>
      </div>

      <div className="contact-layout">
        {/* Left: Contact Info & Socials */}
        <div className="contact-card info-card">
          <h3>{t('contact.infoTitle')}</h3>
          <p className="info-intro">{t('contact.infoDesc')}</p>

          <div className="info-items">
            <a href="mailto:akinevo0@gmail.com" className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-envelope"></i>
              </div>
              <div>
                <span className="info-label">{t('contact.emailLabel')}</span>
                <span className="info-val">akinevo0@gmail.com</span>
              </div>
            </a>

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-location-dot"></i>
              </div>
              <div>
                <span className="info-label">{t('contact.locationLabel')}</span>
                <span className="info-val">{t('contact.locationVal')}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-clock"></i>
              </div>
              <div>
                <span className="info-label">{t('contact.hoursLabel')}</span>
                <span className="info-val">{t('contact.hoursVal')}</span>
              </div>
            </div>
          </div>

          <div className="social-box">
            <h4>Sosyal Medya</h4>
            <div className="social-links">
              <a href="https://www.linkedin.com/company/akinevo/?viewAsMember=true" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fa-brands fa-linkedin-in"></i>
              </a>
              <a href="https://www.instagram.com/akinevo0/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fa-brands fa-instagram"></i>
              </a>
              <a href="https://x.com/Akinevo" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                <i className="fa-brands fa-x-twitter"></i>
              </a>
              <a href="https://www.tiktok.com/@akinevo" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <i className="fa-brands fa-tiktok"></i>
              </a>
              <a href="https://www.youtube.com/channel/UCQq0j39bhW9x38xqwAetkCw" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <i className="fa-brands fa-youtube"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Right: Interactive Contact Form */}
        <div className="contact-card form-card">
          <h3>{t('contact.formTitle')}</h3>
          {submitted ? (
            <div className="form-success">
              <i className="fa-solid fa-circle-check"></i>
              <h4>{t('contact.successTitle')}</h4>
              <p>{t('contact.successDesc')}</p>
              <button className="btn btn-secondary" onClick={() => setSubmitted(false)} style={{ marginTop: '16px' }}>
                {t('contact.sendNewBtn')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              {/* Görünmez Bot Tuzağı (Honeypot) */}
              <input
                type="text"
                name="website_hp"
                value={formData.website_hp}
                onChange={handleChange}
                style={{ display: 'none', position: 'absolute', left: '-9999px' }}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t('contact.labelName')} <span className="required-star">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    maxLength="70"
                    placeholder={t('contact.placeholderName')}
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">{t('contact.labelPhone')} <span className="required-star">*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    maxLength="25"
                    placeholder={t('contact.placeholderPhone')}
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">{t('contact.labelEmail')} <span className="required-star">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    maxLength="100"
                    placeholder={t('contact.placeholderEmail')}
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('contact.labelSubject')} <span className="required-star">*</span></label>
                  <div className="select-wrapper">
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="form-select"
                    >
                      {subjectOptions.map((opt, idx) => (
                        <option key={idx} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <i className="fa-solid fa-chevron-down select-chevron"></i>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('contact.labelMessage')}</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  required
                  maxLength="3000"
                  placeholder={t('contact.placeholderMessage')}
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>

              {validationError && (
                <div className="form-error-msg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', borderColor: '#ef4444', color: '#fca5a5' }}>
                  <i className="fa-solid fa-shield-halved"></i>
                  <span>{validationError}</span>
                </div>
              )}

              {error && (
                <div className="form-error-msg">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  <span>{t('contact.errorMsg')}</span>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary form-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                    <span>{t('contact.btnSending')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('contact.btnSend')}</span>
                    <i className="fa-solid fa-paper-plane"></i>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Global Map Embed Container */}
      <div className="map-wrapper">
        <div className="map-header-content">
          <div className="map-badge">
            <i className="fa-solid fa-earth-americas"></i>
            <span>{t('contact.mapGlobalBadge')}</span>
          </div>
          <h3 className="map-headline">{t('contact.mapGlobalTitle')}</h3>
          <p className="map-subheadline">{t('contact.mapGlobalSubtitle')}</p>

          {/* Harita / Uydu Modu Değiştirici Butonları */}
          <div className="map-mode-toggle">
            <button
              type="button"
              className={`map-mode-btn ${mapMode === 'satellite' ? 'active' : ''}`}
              onClick={() => handleMapModeChange('satellite')}
              aria-label="Uydu Görünümü"
            >
              <i className="fa-solid fa-satellite"></i>
              <span>{t('contact.mapModeSatellite')}</span>
            </button>
            <button
              type="button"
              className={`map-mode-btn ${mapMode === 'roadmap' ? 'active' : ''}`}
              onClick={() => handleMapModeChange('roadmap')}
              aria-label="Harita Görünümü"
            >
              <i className="fa-solid fa-map"></i>
              <span>{t('contact.mapModeRoad')}</span>
            </button>
          </div>
        </div>

        <div className="map-frame-container">
          <div className="map-floating-tag">
            <i className={mapMode === 'satellite' ? 'fa-solid fa-satellite' : 'fa-solid fa-globe'}></i>
            <span>{mapMode === 'satellite' ? 'Live Satellite View' : 'Global Web & Cloud Operations'}</span>
          </div>
          <iframe
            key={mapMode}
            title="Dünya Haritası - Küresel Erişim"
            src={
              mapMode === 'satellite'
                ? "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15000000!2d35.0!3d39.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2str!4v1700000000000!5m2!1sen!2str"
                : "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15000000!2d35.0!3d39.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2str!4v1700000000000!5m2!1sen!2str"
            }
            width="100%"
            height="360"
            style={{ border: 0, borderRadius: '16px' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

export default Contact;
